import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { formatBytes, formatDateTime } from '@/utils/formatters';
import { Lock, FileText, HardDrive, User, Mail, Calendar } from 'lucide-react';

const encryptionLabels: Record<string, string> = {
  zero_knowledge: 'Zero-Knowledge',
  server_side: 'Server-Side',
};

const typeLabels: Record<string, string> = {
  employee: 'Employé',
  folder: 'Dossier',
  company: 'Entreprise',
};

interface VaultStatusCardProps {
  vault: any;
}

export function VaultStatusCard({ vault }: VaultStatusCardProps) {
  const entityType = vault.entityType || vault.type || '';
  const encryptionMode = vault.encryptionMode || vault.mode || '';
  const status = vault.status || '';
  const documentCount = vault.documentCount ?? 0;
  const owner = vault.owner;

  return (
    <Card>
      <CardHeader title="Informations" />
      <CardContent>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-500/20">
              <Lock className="h-6 w-6 text-primary-400" />
            </div>
            <div>
              <p className="font-semibold text-neutral-900 dark:text-neutral-100">{vault.name}</p>
              <div className="flex flex-wrap gap-2 mt-1">
                <Badge variant={status === 'active' ? 'success' : status === 'pending_user_activation' ? 'warning' : 'neutral'}>{status}</Badge>
                {entityType && <Badge variant="info">{typeLabels[entityType] || entityType}</Badge>}
                {encryptionMode && <Badge variant={encryptionMode === 'zero_knowledge' ? 'warning' : 'neutral'}>{encryptionLabels[encryptionMode] || encryptionMode}</Badge>}
              </div>
            </div>
          </div>

          {owner && (
            <div className="space-y-2 pt-2 border-t border-neutral-200 dark:border-neutral-700">
              <div className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400">
                <User className="h-3.5 w-3.5 text-neutral-400" />
                <span>{owner.firstName} {owner.lastName}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400">
                <Mail className="h-3.5 w-3.5 text-neutral-400" />
                <span>{owner.email}</span>
              </div>
            </div>
          )}

          {(vault.activatedAt || vault.createdAt) && (
            <div className="space-y-1">
              {vault.activatedAt && (
                <div className="flex items-center gap-2 text-xs text-neutral-500">
                  <Calendar className="h-3 w-3" />
                  <span>Activé le {formatDateTime(vault.activatedAt)}</span>
                </div>
              )}
              {vault.createdAt && (
                <div className="flex items-center gap-2 text-xs text-neutral-500">
                  <Calendar className="h-3 w-3" />
                  <span>Créé le {formatDateTime(vault.createdAt)}</span>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg bg-neutral-100 dark:bg-neutral-800/50 p-3 text-center">
              <FileText className="h-4 w-4 text-neutral-500 mx-auto mb-1" />
              <p className="text-lg font-bold text-neutral-900 dark:text-neutral-100">{documentCount}</p>
              <p className="text-[10px] text-neutral-500">Documents</p>
            </div>
            <div className="rounded-lg bg-neutral-100 dark:bg-neutral-800/50 p-3 text-center">
              <HardDrive className="h-4 w-4 text-neutral-500 mx-auto mb-1" />
              <p className="text-lg font-bold text-neutral-900 dark:text-neutral-100">{formatBytes(vault.storageUsed)}</p>
              <p className="text-[10px] text-neutral-500">Stockage</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
